import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import bwipjs from 'bwip-js';
import { jsPDF } from 'jspdf';
import { StaffMember, CardTemplate, User, UserRole } from '../types';
import { getStaff, deleteStaff, getTemplateByType } from '../services/storage';
import { drawCardOnCanvas, downloadCanvas } from '../utils/cardUtils';
import { 
  Users, UserCheck, ShieldCheck, Search, Filter, Calendar, Trash2, 
  Download, Printer, Check, CheckSquare, Square, RefreshCw, AlertCircle, Loader, X
} from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface StaffManagerProps {
  currentUser?: User;
}

const StaffManager: React.FC<StaffManagerProps> = ({ currentUser }) => {
  const isViewOnly = currentUser?.role === UserRole.VIEW_ONLY_ADMIN;
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'faculty' | 'coordinator'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Selection states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Barcode printing states
  const [printingItems, setPrintingItems] = useState<{ id: string; name: string; serialNo: string; role: string; barcodeUrl: string }[]>([]);
  const [isGeneratingBarcodes, setIsGeneratingBarcodes] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printLayout, setPrintLayout] = useState<'A4' | '4x6'>('A4');

  // Template states
  const [templates, setTemplates] = useState<Record<string, CardTemplate | null>>({
    faculty: null,
    coordinator: null
  });
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Hidden canvas for batch rendering
  const batchCanvasRef = useRef<HTMLCanvasElement>(null);
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  // Fetch staff and templates on load
  const fetchData = async () => {
    setLoading(true);
    try {
      const [allStaff, facultyTemp, coordTemp] = await Promise.all([
        getStaff(),
        getTemplateByType('faculty'),
        getTemplateByType('coordinator')
      ]);

      setStaff(allStaff.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()));
      setTemplates({
        faculty: facultyTemp,
        coordinator: coordTemp
      });
    } catch (err) {
      console.error("Error fetching staff data:", err);
    } finally {
      setLoading(false);
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter staff list
  const filteredStaff = staff.filter(member => {
    // Role tab check
    if (activeTab !== 'all' && member.role !== activeTab) return false;

    // Search query check
    const term = searchTerm.toLowerCase();
    const matchesSearch = 
      member.name.toLowerCase().includes(term) ||
      member.serialNo.toLowerCase().includes(term) ||
      member.phone.includes(term) ||
      member.whatsapp.includes(term);

    if (!matchesSearch) return false;

    // Date filter check (YYYY-MM-DD)
    if (dateFilter) {
      const regDate = member.submittedAt.split('T')[0];
      if (regDate !== dateFilter) return false;
    }

    return true;
  });

  // Toggle selection
  const handleToggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // Select all visible staff
  const handleSelectAll = () => {
    const visibleIds = filteredStaff.map(s => s.id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));

    const next = new Set(selectedIds);
    if (allSelected) {
      visibleIds.forEach(id => next.delete(id));
    } else {
      visibleIds.forEach(id => next.add(id));
    }
    setSelectedIds(next);
  };

  // Delete handler
  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete registration for ${name}?`)) {
      try {
        await deleteStaff(id);
        setStaff(prev => prev.filter(s => s.id !== id));
        const nextSelected = new Set(selectedIds);
        nextSelected.delete(id);
        setSelectedIds(nextSelected);
      } catch (err) {
        alert("Failed to delete member");
      }
    }
  };

  const exportStaffCSV = (roleType: 'faculty' | 'coordinator') => {
    try {
      const targetStaff = staff.filter(s => s.role === roleType);
      
      if (targetStaff.length === 0) {
        alert(`No registrations found for ${roleType === 'faculty' ? 'Faculty' : 'Coordinator'}.`);
        return;
      }

      // 1. Helper to escape CSV
      const escapeCsv = (str: string) => {
          if (str === undefined || str === null) return '';
          const s = String(str);
          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
              return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
      };

      // 2. Headers
      const headers = [
        'Sl No',
        'Name',
        'Phone Number',
        'WhatsApp Number',
        'Role',
        'Registration Date'
      ];

      const rows: string[][] = [];

      // 3. Populate rows
      targetStaff.forEach(m => {
        const regDate = m.submittedAt ? new Date(m.submittedAt).toLocaleDateString() : '';
        rows.push([
          escapeCsv(m.serialNo),
          escapeCsv(m.name),
          escapeCsv(m.phone),
          escapeCsv(m.whatsapp),
          escapeCsv(roleType === 'faculty' ? 'Faculty' : 'Coordinator'),
          escapeCsv(regDate)
        ]);
      });

      const csvContent = "data:text/csv;charset=utf-8," 
        + "\uFEFF" 
        + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${roleType}_list_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Staff CSV export failed", e);
      alert("Failed to export staff list.");
    }
  };

  // Helper to draw a card and get its Blob
  const getCardBlob = (member: StaffMember, template: CardTemplate): Promise<Blob | null> => {
    return new Promise((resolve) => {
      (async () => {
        if (!batchCanvasRef.current) return resolve(null);
        
        const canvas = batchCanvasRef.current;
        const mergedData = {
          serialNo: member.serialNo,
          idNo: member.serialNo,
          name: member.name,
          headName: member.name,
          phone: member.phone,
          whatsapp: member.whatsapp,
          housePhoto: member.photo || '',
          categoryName: member.role === 'faculty' ? 'Faculty (ഫാക്കൽറ്റി)' : 'Coordinator (കോർഡിനേറ്റർ)',
          role: member.role
        };

        try {
          await drawCardOnCanvas(canvas, template, mergedData);
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/png');
        } catch (e) {
          console.error("Error zipping card for", member.name, e);
          resolve(null);
        }
      })();
    });
  };

  // Batch download ZIP handler
  const handleBatchDownload = async (targetType: 'all_selected' | 'all_filtered') => {
    const listToProcess = targetType === 'all_selected' 
      ? staff.filter(s => selectedIds.has(s.id))
      : filteredStaff;

    if (listToProcess.length === 0) {
      alert("No staff members selected or matches standard query.");
      return;
    }

    // Verify templates are loaded
    const missingFaculty = listToProcess.some(s => s.role === 'faculty') && !templates.faculty;
    const missingCoord = listToProcess.some(s => s.role === 'coordinator') && !templates.coordinator;

    if (missingFaculty || missingCoord) {
      alert("Error: Card templates for Faculty or Coordinator are not configured. Please design and save them in ID Card Mgmt first.");
      return;
    }

    setZipping(true);
    setZipProgress(0);

    const zip = new JSZip();
    let processed = 0;

    for (const member of listToProcess) {
      const template = templates[member.role];
      if (template) {
        const blob = await getCardBlob(member, template);
        if (blob) {
          const folderName = member.role === 'faculty' ? 'Faculty_ID_Cards' : 'Coordinator_ID_Cards';
          const filename = `${member.serialNo}_${member.name.replace(/\s+/g, '_')}.png`;
          zip.folder(folderName)?.file(filename, blob);
        }
      }
      processed++;
      setZipProgress(Math.round((processed / listToProcess.length) * 100));
    }

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      const archiveName = targetType === 'all_selected' 
        ? `Selected_Staff_ID_Cards_${new Date().toISOString().split('T')[0]}.zip`
        : `Filtered_Staff_ID_Cards_${new Date().toISOString().split('T')[0]}.zip`;

      saveAs(content, archiveName);
    } catch (e) {
      console.error("ZIP Generation error:", e);
      alert("Failed to generate ZIP archive.");
    } finally {
      setZipping(false);
      setZipProgress(0);
    }
  };

  // Generate barcodes and prepare print preview
  const handlePreparePrintBarcodes = async (targetType: 'selected' | 'filtered') => {
    const listToProcess = targetType === 'selected'
      ? staff.filter(s => selectedIds.has(s.id))
      : filteredStaff;

    if (listToProcess.length === 0) {
      alert("No staff members selected or matching standard filters.");
      return;
    }

    setIsGeneratingBarcodes(true);
    const items: { id: string; name: string; serialNo: string; role: string; barcodeUrl: string }[] = [];

    for (const member of listToProcess) {
      try {
        const canvas = document.createElement('canvas');
        await bwipjs.toCanvas(canvas, {
          bcid: 'code128',
          text: member.serialNo,
          scale: 4,
          height: 12,
          includetext: true,
          textxalign: 'center',
        });
        const barcodeUrl = canvas.toDataURL('image/png');
        items.push({
          id: member.id,
          name: member.name,
          serialNo: member.serialNo,
          role: member.role,
          barcodeUrl
        });
      } catch (err) {
        console.error("Failed to generate barcode for", member.serialNo, err);
        items.push({
          id: member.id,
          name: member.name,
          serialNo: member.serialNo,
          role: member.role,
          barcodeUrl: ''
        });
      }
    }

    setPrintingItems(items);
    setIsGeneratingBarcodes(false);
    setShowPrintModal(true);
  };

  // Generate barcodes and download A4 PDF sheet directly
  const handleDownloadA4Sheet = async (targetType: 'selected' | 'filtered') => {
    const listToProcess = targetType === 'selected'
      ? staff.filter(s => selectedIds.has(s.id))
      : filteredStaff;

    if (listToProcess.length === 0) {
      alert("No staff members selected or matching standard filters.");
      return;
    }

    setIsGeneratingBarcodes(true);
    const items: { id: string; name: string; serialNo: string; role: string; barcodeUrl: string }[] = [];

    for (const member of listToProcess) {
      try {
        const canvas = document.createElement('canvas');
        await bwipjs.toCanvas(canvas, {
          bcid: 'code128',
          text: member.serialNo,
          scale: 4,
          height: 12,
          includetext: true,
          textxalign: 'center',
        });
        const barcodeUrl = canvas.toDataURL('image/png');
        items.push({
          id: member.id,
          name: member.name,
          serialNo: member.serialNo,
          role: member.role,
          barcodeUrl
        });
      } catch (err) {
        console.error("Failed to generate barcode for", member.serialNo, err);
        items.push({
          id: member.id,
          name: member.name,
          serialNo: member.serialNo,
          role: member.role,
          barcodeUrl: ''
        });
      }
    }

    setIsGeneratingBarcodes(false);
    await downloadCombinedA4Pdf(items);
  };

  // Helper to download an individual sticker in the exact 4" x 6" ratio as a PNG
  const downloadStickerImage = (item: { name: string; serialNo: string; role: string; barcodeUrl: string }) => {
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 1200;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 1. Header Text
    ctx.fillStyle = '#1e293b'; // slate-800
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Malayalam Header
    ctx.font = '700 42px "Inter", "sans-serif"';
    ctx.fillText('Malayalee Samajam', canvas.width / 2, 140);

    // Sub Header
    ctx.fillStyle = '#94a3b8'; // slate-400
    ctx.font = '700 24px "Inter", "sans-serif"';
    ctx.fillText('CAMP STAFF 2026', canvas.width / 2, 195);

    // 2. Barcode Image
    if (item.barcodeUrl) {
      const barcodeImg = new Image();
      barcodeImg.onload = () => {
        // Draw barcode image in center
        const bcWidth = 640;
        const bcHeight = 210;
        const bcX = (canvas.width - bcWidth) / 2;
        const bcY = 280; // Shorter distance from top, tightly styled
        ctx.drawImage(barcodeImg, bcX, bcY, bcWidth, bcHeight);

        // 3. Staff Name (placed higher to reduce gap)
        ctx.fillStyle = '#0f172a'; // slate-900
        ctx.font = '900 52px "Inter", "sans-serif"';
        ctx.fillText(item.name.toUpperCase(), canvas.width / 2, 575);

        // 4. Badges (Role & Serial Number)
        const roleText = item.role === 'faculty' ? 'FACULTY' : 'COORDINATOR';
        const serialText = item.serialNo;

        ctx.font = '700 24px "Inter", "sans-serif"';
        const roleWidth = ctx.measureText(roleText).width + 40;
        const serialWidth = ctx.measureText(serialText).width + 40;

        const gap = 24;
        const totalBadgesWidth = roleWidth + serialWidth + gap;
        const startX = (canvas.width - totalBadgesWidth) / 2;

        // Draw Role Badge
        ctx.fillStyle = '#f8fafc'; // slate-50
        ctx.strokeStyle = '#e2e8f0'; // slate-200
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(startX, 645, roleWidth, 60, 10);
        } else {
          ctx.rect(startX, 645, roleWidth, 60);
        }
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#475569'; // slate-600
        ctx.fillText(roleText, startX + roleWidth / 2, 675);

        // Draw Serial Badge
        ctx.fillStyle = '#f8fafc'; // slate-50
        ctx.strokeStyle = '#e2e8f0'; // slate-200
        
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(startX + roleWidth + gap, 645, serialWidth, 60, 10);
        } else {
          ctx.rect(startX + roleWidth + gap, 645, serialWidth, 60);
        }
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#0f172a'; // slate-900
        ctx.fillText(serialText, startX + roleWidth + gap + serialWidth / 2, 675);

        // 5. Footer Text
        ctx.fillStyle = '#cbd5e1'; // slate-300
        ctx.font = '700 20px "Inter", "sans-serif"';
        ctx.fillText('MALAYALEE SAMAJAM CAMP MANAGEMENT SYSTEM', canvas.width / 2, 1050);

        // Download trigger
        const link = document.createElement('a');
        link.download = `sticker_${item.serialNo}_${item.name.replace(/\s+/g, '_')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      };
      barcodeImg.src = item.barcodeUrl;
    }
  };

  // Helper to generate and download sticker(s) as a true 4" x 6" PDF
  const downloadCombinedPdf = async (items: typeof printingItems) => {
    if (items.length === 0) return;
    setIsPdfGenerating(true);

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: [4, 6]
      });

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 1200;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Header Malayalam Text
        ctx.fillStyle = '#1e293b';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '700 42px "Inter", "sans-serif"';
        ctx.fillText('Malayalee Samajam', canvas.width / 2, 140);

        // Header Sub Text
        ctx.fillStyle = '#94a3b8';
        ctx.font = '700 24px "Inter", "sans-serif"';
        ctx.fillText('CAMP STAFF 2026', canvas.width / 2, 195);

        if (item.barcodeUrl) {
          await new Promise<void>((resolve) => {
            const barcodeImg = new Image();
            barcodeImg.onload = () => {
              const bcWidth = 640;
              const bcHeight = 210;
              const bcX = (canvas.width - bcWidth) / 2;
              const bcY = 280;
              ctx.drawImage(barcodeImg, bcX, bcY, bcWidth, bcHeight);

              // Staff Name
              ctx.fillStyle = '#0f172a';
              ctx.font = '900 52px "Inter", "sans-serif"';
              ctx.fillText(item.name.toUpperCase(), canvas.width / 2, 575);

              // Role and Serial Code Badges
              const roleText = item.role === 'faculty' ? 'FACULTY' : 'COORDINATOR';
              const serialText = item.serialNo;

              ctx.font = '700 24px "Inter", "sans-serif"';
              const roleWidth = ctx.measureText(roleText).width + 40;
              const serialWidth = ctx.measureText(serialText).width + 40;

              const gap = 24;
              const totalBadgesWidth = roleWidth + serialWidth + gap;
              const startX = (canvas.width - totalBadgesWidth) / 2;

              // Draw Role Badge
              ctx.fillStyle = '#f8fafc';
              ctx.strokeStyle = '#e2e8f0';
              ctx.lineWidth = 2;

              ctx.beginPath();
              if (ctx.roundRect) {
                ctx.roundRect(startX, 645, roleWidth, 60, 10);
              } else {
                ctx.rect(startX, 645, roleWidth, 60);
              }
              ctx.fill();
              ctx.stroke();

              ctx.fillStyle = '#475569';
              ctx.fillText(roleText, startX + roleWidth / 2, 675);

              // Draw Serial Badge
              ctx.fillStyle = '#f8fafc';
              ctx.strokeStyle = '#e2e8f0';

              ctx.beginPath();
              if (ctx.roundRect) {
                ctx.roundRect(startX + roleWidth + gap, 645, serialWidth, 60, 10);
              } else {
                ctx.rect(startX + roleWidth + gap, 645, serialWidth, 60);
              }
              ctx.fill();
              ctx.stroke();

              ctx.fillStyle = '#0f172a';
              ctx.fillText(serialText, startX + roleWidth + gap + serialWidth / 2, 675);

              // Footer Branding
              ctx.fillStyle = '#cbd5e1';
              ctx.font = '700 20px "Inter", "sans-serif"';
              ctx.fillText('MALAYALEE SAMAJAM CAMP MANAGEMENT SYSTEM', canvas.width / 2, 1050);

              resolve();
            };
            barcodeImg.onerror = () => resolve();
            barcodeImg.src = item.barcodeUrl;
          });
        }

        if (i > 0) {
          doc.addPage([4, 6], 'portrait');
        }

        const imgData = canvas.toDataURL('image/png');
        doc.addImage(imgData, 'PNG', 0, 0, 4, 6);
      }

      const filename = items.length === 1 
        ? `sticker_${items[0].serialNo}_${items[0].name.replace(/\s+/g, '_')}.pdf`
        : `barcodes_stickers_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error("Failed to generate PDF", error);
      alert("An error occurred while generating the PDF sticker(s).");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  // Helper to generate and download sticker(s) arranged on standard A4 paper (210mm x 297mm) as a 24-sticker sheet (3x8 grid)
  const downloadCombinedA4Pdf = async (items: typeof printingItems) => {
    if (items.length === 0) return;
    setIsPdfGenerating(true);

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Sticker layout configuration on A4 sheet (3 cols, 8 rows = 24 stickers)
      const colWidth = 63.5;  // 63.5 mm width
      const rowHeight = 34.0; // 34 mm height
      const colGap = 2.0;     // 2 mm gap between columns
      const rowGap = 2.0;     // 2 mm gap between rows
      const marginLeft = 7.75; // Left margin so 3 columns are perfectly centered on 210mm page
      const marginTop = 5.5;   // Top margin so 8 rows are perfectly centered on 297mm page

      const stickersPerPage = 24;
      const numPages = Math.ceil(items.length / stickersPerPage);

      // Helper to render a high-res sticker canvas for a 3x8 single slot (744x400 px, ~1.86:1 ratio)
      const getStickerImgData = async (item: typeof printingItems[0]): Promise<string> => {
        const canvas = document.createElement('canvas');
        canvas.width = 744;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Header Malayalam Text (Malayalee Samajam)
        ctx.fillStyle = '#334155'; // slate-700
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '700 24px "Inter", "sans-serif"';
        ctx.fillText('Malayalee Samajam', canvas.width / 2, 35);

        // Header Sub Text
        ctx.fillStyle = '#64748b'; // slate-500
        ctx.font = '700 14px "Inter", "sans-serif"';
        ctx.fillText('CAMP STAFF 2026', canvas.width / 2, 65);

        if (item.barcodeUrl) {
          await new Promise<void>((resolve) => {
            const barcodeImg = new Image();
            barcodeImg.onload = () => {
              // Draw barcode image in the middle
              const bcWidth = 460;
              const bcHeight = 140;
              const bcX = (canvas.width - bcWidth) / 2;
              const bcY = 90;
              ctx.drawImage(barcodeImg, bcX, bcY, bcWidth, bcHeight);

              // Staff Name under barcode
              ctx.fillStyle = '#0f172a'; // slate-900
              ctx.font = '900 32px "Inter", "sans-serif"';
              ctx.fillText(item.name.toUpperCase(), canvas.width / 2, 275);

              // Role and Serial Code
              const roleText = item.role === 'faculty' ? 'FACULTY' : 'COORDINATOR';
              const serialText = item.serialNo;
              ctx.fillStyle = '#475569'; // slate-600
              ctx.font = '700 20px "Inter", "sans-serif"';
              ctx.fillText(`${roleText}  •  ${serialText}`, canvas.width / 2, 325);

              // Footer Branding (very small)
              ctx.fillStyle = '#94a3b8'; // slate-400
              ctx.font = '700 13px "Inter", "sans-serif"';
              ctx.fillText('MALAYALEE SAMAJAM CAMP MANAGEMENT SYSTEM', canvas.width / 2, 365);

              resolve();
            };
            barcodeImg.onerror = () => resolve();
            barcodeImg.src = item.barcodeUrl;
          });
        }

        return canvas.toDataURL('image/png');
      };

      for (let page = 0; page < numPages; page++) {
        if (page > 0) {
          doc.addPage('a4', 'portrait');
        }

        const startIndex = page * stickersPerPage;
        const endIndex = Math.min(startIndex + stickersPerPage, items.length);

        for (let idx = startIndex; idx < endIndex; idx++) {
          const item = items[idx];
          const localIndex = idx - startIndex;
          const col = localIndex % 3;
          const row = Math.floor(localIndex / 3);

          const x = marginLeft + col * (colWidth + colGap);
          const y = marginTop + row * (rowHeight + rowGap);

          const imgData = await getStickerImgData(item);
          if (imgData) {
            doc.addImage(imgData, 'PNG', x, y, colWidth, rowHeight);

            // Draw a subtle cut border
            doc.setDrawColor(226, 232, 240); // slate-200
            doc.setLineWidth(0.1);
            doc.rect(x, y, colWidth, rowHeight);
          }
        }
      }

      const filename = items.length === 1 
        ? `sticker_A4_${items[0].serialNo}_${items[0].name.replace(/\s+/g, '_')}.pdf`
        : `barcodes_A4_stickers_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error("Failed to generate A4 PDF", error);
      alert("An error occurred while generating the A4 PDF.");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden batch canvas */}
      <canvas ref={batchCanvasRef} className="hidden" />

      {/* Header Panel */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800 flex items-center gap-2">
            <Users className="text-blue-600" /> Staff Management (ഫാക്കൽറ്റി/കോർഡിനേറ്റർ)
          </h1>
          <p className="text-slate-500 text-xs mt-1">
            Manage registrations, configure templates, and download/print professional staff ID cards.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => exportStaffCSV('faculty')}
            className="flex items-center gap-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 px-4 py-2.5 rounded-lg transition"
          >
            <Download size={14} /> Export Faculty CSV
          </button>
          <button 
            onClick={() => exportStaffCSV('coordinator')}
            className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-4 py-2.5 rounded-lg transition"
          >
            <Download size={14} /> Export Coordinator CSV
          </button>
          <button 
            onClick={fetchData}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 px-4 py-2.5 rounded-lg transition"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Sync Data
          </button>
        </div>
      </div>

      {/* Quick Stats Panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-5 rounded-xl text-white shadow">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Staff Registered</p>
          <h3 className="text-3xl font-black mt-1">{staff.length}</h3>
          <p className="text-[10px] text-slate-400 mt-2">Active members</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Faculty (അദ്ധ്യാപകർ)</p>
            <h3 className="text-2xl font-black text-slate-800 mt-1">
              {staff.filter(s => s.role === 'faculty').length}
            </h3>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
            <UserCheck size={20} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Coordinators (കോർഡിനേറ്റർമാർ)</p>
            <h3 className="text-2xl font-black text-slate-800 mt-1">
              {staff.filter(s => s.role === 'coordinator').length}
            </h3>
          </div>
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <ShieldCheck size={20} />
          </div>
        </div>
      </div>

      {/* Card Template Warnings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!templates.faculty && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex gap-3 text-amber-900 text-xs">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
            <div>
              <p className="font-bold">Faculty Card Template Missing</p>
              <p className="text-[11px] leading-relaxed mt-0.5">
                No Faculty background template is configured yet. Please go to <b>ID Card Mgmt</b>, select "Faculty Card", and configure the background and text fields.
              </p>
            </div>
          </div>
        )}
        {!templates.coordinator && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex gap-3 text-amber-900 text-xs">
            <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
            <div>
              <p className="font-bold">Coordinator Card Template Missing</p>
              <p className="text-[11px] leading-relaxed mt-0.5">
                No Coordinator background template is configured yet. Please go to <b>ID Card Mgmt</b>, select "Coordinator Card", and configure the background and text fields.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Filters & Actions Panel */}
      <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-4">
        {/* Row 1: Search & Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-3.5 text-slate-400" size={16} />
            <input 
              type="text" 
              placeholder="Search name, phone, qualifications..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 p-3 text-sm focus:ring-2 focus:ring-blue-400 outline-none transition"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-3.5 text-slate-400" size={16} />
            <input 
              type="date" 
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 p-3 text-sm focus:ring-2 focus:ring-blue-400 outline-none transition"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
            />
            {dateFilter && (
              <button onClick={() => setDateFilter('')} className="absolute right-3 top-3.5 text-xs font-bold text-slate-400 hover:text-slate-600">Clear</button>
            )}
          </div>

          <div className="flex bg-slate-100 p-1 rounded-xl">
            {(['all', 'faculty', 'coordinator'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setSelectedIds(new Set());
                }}
                className={`flex-1 py-2 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                  activeTab === tab 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab === 'all' ? 'All Staff' : tab}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Batch Printing Actions */}
        {!isViewOnly && (
          <div className="border-t pt-4 flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={handleSelectAll}
                className="text-xs font-extrabold text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 px-4 py-2.5 rounded-lg transition flex items-center gap-1.5"
              >
                {filteredStaff.every(s => selectedIds.has(s.id)) ? (
                  <>Deselect All</>
                ) : (
                  <>Select All ({filteredStaff.length})</>
                )}
              </button>

              <span className="text-xs font-bold text-slate-400">
                {selectedIds.size} selected for printing
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Download Selected Bundle */}
              <button
                onClick={() => handleBatchDownload('all_selected')}
                disabled={selectedIds.size === 0 || zipping}
                className="text-xs font-extrabold bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
              >
                {zipping ? (
                  <>
                    <Loader className="animate-spin" size={14} /> ZIP ({zipProgress}%)
                  </>
                ) : (
                  <>
                    <Download size={14} /> Download Selected ({selectedIds.size})
                  </>
                )}
              </button>

              {/* Download Barcode Sheet (A4) for Selected */}
              <button
                onClick={() => handleDownloadA4Sheet('selected')}
                disabled={selectedIds.size === 0 || isGeneratingBarcodes || zipping}
                className="text-xs font-extrabold bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
                title="Download selected staff barcode stickers arranged on A4 sheet (24 per page)"
              >
                {isGeneratingBarcodes ? (
                  <>
                    <Loader className="animate-spin" size={14} /> Generating...
                  </>
                ) : (
                  <>
                    <Download size={14} /> Download A4 Barcode Sheet ({selectedIds.size})
                  </>
                )}
              </button>

              {/* Print Selected Barcodes */}
              <button
                onClick={() => handlePreparePrintBarcodes('selected')}
                disabled={selectedIds.size === 0 || isGeneratingBarcodes || zipping}
                className="text-xs font-extrabold bg-slate-800 text-white hover:bg-slate-900 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
              >
                {isGeneratingBarcodes ? (
                  <>
                    <Loader className="animate-spin" size={14} /> Generating...
                  </>
                ) : (
                  <>
                    <Printer size={14} /> Print/Preview Barcodes ({selectedIds.size})
                  </>
                )}
              </button>

              {/* Download All Filtered Bundle */}
              <button
                onClick={() => handleBatchDownload('all_filtered')}
                disabled={filteredStaff.length === 0 || zipping}
                className="text-xs font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
              >
                {zipping ? (
                  <>
                    <Loader className="animate-spin" size={14} /> ZIP ({zipProgress}%)
                  </>
                ) : (
                  <>
                    <Download size={14} /> Download All Cards ({filteredStaff.length})
                  </>
                )}
              </button>

              {/* Download All Barcode Sheet (A4) for Filtered */}
              <button
                onClick={() => handleDownloadA4Sheet('filtered')}
                disabled={filteredStaff.length === 0 || isGeneratingBarcodes || zipping}
                className="text-xs font-extrabold bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
                title="Download all filtered staff barcode stickers arranged on A4 sheet (24 per page)"
              >
                {isGeneratingBarcodes ? (
                  <>
                    <Loader className="animate-spin" size={14} /> Generating...
                  </>
                ) : (
                  <>
                    <Download size={14} /> Download All A4 Barcode Sheets ({filteredStaff.length})
                  </>
                )}
              </button>

              {/* Print All Filtered Barcodes */}
              <button
                onClick={() => handlePreparePrintBarcodes('filtered')}
                disabled={filteredStaff.length === 0 || isGeneratingBarcodes || zipping}
                className="text-xs font-extrabold bg-blue-700 text-white hover:bg-blue-800 disabled:bg-slate-100 disabled:text-slate-400 px-5 py-2.5 rounded-lg transition shadow-sm flex items-center gap-1.5"
              >
                {isGeneratingBarcodes ? (
                  <>
                    <Loader className="animate-spin" size={14} /> Generating...
                  </>
                ) : (
                  <>
                    <Printer size={14} /> Print/Preview All Barcodes ({filteredStaff.length})
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Staff Table / Grid */}
      {loading ? (
        <div className="bg-white p-16 rounded-xl border text-center text-slate-500">
          <Loader className="animate-spin inline mr-2" /> Loading staff records...
        </div>
      ) : filteredStaff.length === 0 ? (
        <div className="bg-white p-16 rounded-xl border border-dashed text-center text-slate-400 italic">
          No staff registration records found matching standard criteria.
        </div>
      ) : (
        <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[10px] font-black uppercase tracking-wider border-b">
                  {!isViewOnly && <th className="p-4 w-12 text-center">Select</th>}
                  <th className="p-4 w-24">Photo</th>
                  <th className="p-4">Serial / ID</th>
                  <th className="p-4">Name</th>
                  <th className="p-4">Role</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Reg. Date</th>
                  {!isViewOnly && <th className="p-4 w-24 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y text-slate-700 text-sm">
                {filteredStaff.map(member => {
                  const isSelected = selectedIds.has(member.id);
                  const isFacultyMember = member.role === 'faculty';

                  return (
                    <tr 
                      key={member.id} 
                      className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50/40' : ''}`}
                    >
                      {!isViewOnly && (
                        <td className="p-4 text-center">
                          <button
                            onClick={() => handleToggleSelect(member.id)}
                            className="text-slate-400 hover:text-blue-600 transition"
                          >
                            {isSelected ? (
                              <CheckSquare className="text-blue-600" size={18} />
                            ) : (
                              <Square size={18} />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="p-4">
                        {member.photo ? (
                          <img 
                            src={member.photo} 
                            alt={member.name} 
                            className="w-12 h-14 object-cover rounded shadow-inner border border-slate-200"
                          />
                        ) : (
                          <div className="w-12 h-14 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-400">
                            No Photo
                          </div>
                        )}
                      </td>
                      <td className="p-4 font-mono font-bold text-slate-800 text-xs">
                        {member.serialNo}
                      </td>
                      <td className="p-4 font-bold text-slate-900">
                        {member.name}
                      </td>
                      <td className="p-4">
                        <span className={`inline-block px-3 py-0.5 rounded-full text-[10px] font-bold ${
                          isFacultyMember 
                            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        }`}>
                          {isFacultyMember ? 'Faculty' : 'Coordinator'}
                        </span>
                      </td>
                      <td className="p-4 space-y-0.5">
                        <p className="font-semibold text-slate-800 text-xs"><b className="text-slate-400">Mob:</b> {member.phone}</p>
                        <p className="font-semibold text-slate-800 text-xs"><b className="text-emerald-600">WA:</b> {member.whatsapp}</p>
                      </td>
                      <td className="p-4 text-slate-400 text-xs font-semibold">
                        {member.submittedAt.split('T')[0]}
                      </td>
                      {!isViewOnly && (
                        <td className="p-4 text-center">
                          <div className="flex justify-center gap-2">
                            {/* Individual Download card */}
                            <button
                              onClick={async () => {
                                const template = templates[member.role];
                                if (!template) {
                                  alert("No template configured for this staff role yet.");
                                  return;
                                }
                                // We can draw and download immediately
                                const canvas = document.createElement('canvas');
                                const mergedData = {
                                  serialNo: member.serialNo,
                                  idNo: member.serialNo,
                                  name: member.name,
                                  headName: member.name,
                                  phone: member.phone,
        whatsapp: member.whatsapp,
                                  housePhoto: member.photo || '',
                                  categoryName: member.role === 'faculty' ? 'Faculty (ഫാക്കൽറ്റി)' : 'Coordinator (കോർഡിനേറ്റർ)',
                                  role: member.role
                                };
                                await drawCardOnCanvas(canvas, template, mergedData);
                                downloadCanvas(canvas, `ID_Card_${member.serialNo}_${member.name.replace(/\s+/g, '_')}.png`);
                              }}
                              title="Download Card"
                              className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                            >
                              <Download size={14} />
                            </button>

                            {/* Individual Print Barcode */}
                            <button
                              onClick={async () => {
                                setIsGeneratingBarcodes(true);
                                try {
                                  const canvas = document.createElement('canvas');
                                  await bwipjs.toCanvas(canvas, {
                                    bcid: 'code128',
                                    text: member.serialNo,
                                    scale: 4,
                                    height: 12,
                                    includetext: true,
                                    textxalign: 'center',
                                  });
                                  const barcodeUrl = canvas.toDataURL('image/png');
                                  setPrintingItems([{
                                    id: member.id,
                                    name: member.name,
                                    serialNo: member.serialNo,
                                    role: member.role,
                                    barcodeUrl
                                  }]);
                                  setShowPrintModal(true);
                                } catch (err) {
                                  console.error("Failed to generate barcode", err);
                                  alert("Failed to generate barcode sticker.");
                                } finally {
                                  setIsGeneratingBarcodes(false);
                                }
                              }}
                              title="Print Barcode Sticker (4x6)"
                              className="p-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
                            >
                              <Printer size={14} />
                            </button>

                            {/* Delete member */}
                            <button
                              onClick={() => handleDelete(member.id, member.name)}
                              title="Delete"
                              className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Barcode Print Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                  <Printer className="text-blue-600" size={20} />
                  {printLayout === 'A4' ? (
                    <>Barcode Sticker Print Preview (A4 Sheet - 24 per page)</>
                  ) : (
                    <>Barcode Sticker Print Preview (4" x 6")</>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {printLayout === 'A4' ? (
                    <>Ready to print {printingItems.length} sticker{printingItems.length > 1 ? 's' : ''}. Optimized for standard A4 sticker sheets (3 columns × 8 rows).</>
                  ) : (
                    <>Ready to print {printingItems.length} sticker{printingItems.length > 1 ? 's' : ''}. Designed for 4" x 6" label printers (Zebra, Rollo, etc.).</>
                  )}
                </p>
              </div>
              <button 
                onClick={() => setShowPrintModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Layout Selector */}
            <div className="px-6 py-3 border-b border-slate-100 flex gap-4 bg-slate-50 items-center">
              <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Print Target Layout:</span>
              <div className="flex bg-slate-200/60 p-1 rounded-xl">
                <button
                  onClick={() => setPrintLayout('A4')}
                  className={`px-4 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                    printLayout === 'A4'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  A4 Sheet (24 Stickers)
                </button>
                <button
                  onClick={() => setPrintLayout('4x6')}
                  className={`px-4 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg transition-all ${
                    printLayout === '4x6'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  4" x 6" Label Card
                </button>
              </div>
            </div>

            {/* Print Instructions */}
            <div className="p-4 bg-blue-50 border-b border-blue-100 px-6 text-blue-950 text-xs flex gap-3">
              <AlertCircle className="text-blue-600 shrink-0 mt-0.5" size={16} />
              <div>
                <p className="font-bold">Printing Instructions:</p>
                {printLayout === 'A4' ? (
                  <p className="text-[11px] leading-relaxed mt-0.5">
                    1. Click the green <b>"Print Now"</b> button below.<br />
                    2. In the browser print dialog, set your <b>Destination</b> to your standard printer.<br />
                    3. Set <b>Paper Size</b> to <b>A4</b>.<br />
                    4. Set <b>Margins</b> to <b>None</b> (extremely critical for perfect alignment!) and select <b>Portrait</b> orientation.<br />
                    5. Ensure <b>"Scale: 100%"</b> or <b>"Actual Size"</b> is selected to prevent shrinking.
                  </p>
                ) : (
                  <p className="text-[11px] leading-relaxed mt-0.5">
                    1. Click the green <b>"Print Now"</b> button below.<br />
                    2. In the browser print dialog, set <b>Destination</b> to your label printer.<br />
                    3. Set <b>Paper Size</b> to <b>4" x 6"</b> (or 100mm x 150mm).<br />
                    4. Set <b>Margins</b> to <b>None</b> and select <b>Portrait</b> orientation.<br />
                    5. Ensure <b>"Fit to page"</b> or <b>"Scale: 100%"</b> is checked for crisp barcodes.
                  </p>
                )}
              </div>
            </div>

            {/* Scrollable Preview Grid */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-100/50 space-y-6">
              {printLayout === 'A4' ? (
                (() => {
                  const a4Pages: typeof printingItems[] = [];
                  for (let i = 0; i < printingItems.length; i += 24) {
                    a4Pages.push(printingItems.slice(i, i + 24));
                  }
                  return a4Pages.map((pageItems, pageIdx) => (
                    <div key={pageIdx} className="bg-white border border-slate-300 rounded-2xl shadow-md p-6 max-w-[620px] mx-auto space-y-4 mb-6 relative">
                      <div className="flex justify-between items-center border-b pb-2 text-[10px] font-black text-slate-400">
                        <span>A4 SHEET - PAGE {pageIdx + 1} OF {a4Pages.length}</span>
                        <span>{pageItems.length} STICKERS ON THIS PAGE</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5 bg-slate-100 p-2 rounded-lg border border-slate-200">
                        {Array.from({ length: 24 }).map((_, index) => {
                          const item = pageItems[index];
                          if (!item) {
                            return (
                              <div key={index} className="aspect-[63.5/34] border border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-white/40 text-[9px] text-slate-300 font-bold italic select-none">
                                Empty Slot
                              </div>
                            );
                          }
                          return (
                            <div key={item.id} className="aspect-[63.5/34] border border-slate-200 rounded-lg p-2 bg-white flex flex-col justify-between hover:border-blue-400 transition shadow-2xs relative group">
                              <div className="text-[6.5px] font-black uppercase text-slate-400 tracking-wider flex justify-between items-center leading-none">
                                <span>Malayalee Samajam</span>
                                <span className="font-mono text-[6px] text-slate-500">{item.serialNo}</span>
                              </div>
                              <div className="flex-1 flex items-center justify-center py-0.5">
                                {item.barcodeUrl ? (
                                  <img src={item.barcodeUrl} alt={item.serialNo} className="h-6 w-auto object-contain max-w-full" />
                                ) : (
                                  <span className="text-[5px] text-red-500 font-bold">No Barcode</span>
                                )}
                              </div>
                              <div className="text-center">
                                <p className="text-[8px] font-black uppercase tracking-tight text-slate-800 truncate leading-tight">{item.name}</p>
                                <p className="text-[5.5px] font-bold text-slate-400 leading-none mt-0.5 uppercase tracking-widest">{item.role === 'faculty' ? 'FACULTY' : 'COOR'}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center">
                  {printingItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-5 shadow-sm flex flex-col justify-center items-center gap-3.5 relative aspect-[4/6] w-full max-w-[240px] mx-auto hover:border-blue-400 transition"
                    >
                      {/* Visual representation card */}
                      <div className="text-center">
                        <h4 className="text-[9px] font-black uppercase tracking-wider text-slate-400">Malayalee Samajam</h4>
                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-0.5 font-sans">CAMP STAFF 2026</p>
                      </div>

                      <div className="py-1 flex flex-col items-center justify-center w-full">
                        {item.barcodeUrl ? (
                          <img 
                            src={item.barcodeUrl} 
                            alt={item.serialNo} 
                            className="w-full h-auto object-contain max-h-[75px]"
                          />
                        ) : (
                          <span className="text-xs text-red-500 font-bold">Error generating barcode</span>
                        )}
                      </div>

                      <div className="text-center w-full">
                        <h2 className="text-base font-black text-slate-800 tracking-tight uppercase truncate px-1" title={item.name}>
                          {item.name}
                        </h2>
                        <div className="mt-1 flex items-center justify-center gap-1">
                          <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-[8px] font-black uppercase tracking-wider rounded font-mono text-slate-600">
                            {item.role === 'faculty' ? 'FAC' : 'COOR'}
                          </span>
                          <span className="text-[8px] font-bold tracking-wider font-mono text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">
                            {item.serialNo}
                          </span>
                        </div>
                      </div>

                      <div className="absolute top-2 left-2 z-10 flex gap-1">
                        <button
                          onClick={() => downloadStickerImage(item)}
                          className="p-1 bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-500 rounded transition shadow-sm flex items-center justify-center"
                          title="Download Sticker as 4x6 PNG"
                          disabled={isPdfGenerating}
                        >
                          <Download size={11} />
                        </button>
                        <button
                          onClick={() => downloadCombinedPdf([item])}
                          className="p-1 bg-slate-100 hover:bg-emerald-600 hover:text-white text-slate-500 rounded transition shadow-sm flex items-center justify-center text-[9px] font-black px-1.5"
                          title="Download Sticker as 4x6 PDF"
                          disabled={isPdfGenerating}
                        >
                          4x6 PDF
                        </button>
                        <button
                          onClick={() => downloadCombinedA4Pdf([item])}
                          className="p-1 bg-slate-100 hover:bg-violet-600 hover:text-white text-slate-500 rounded transition shadow-sm flex items-center justify-center text-[9px] font-black px-1.5"
                          title="Download Sticker as A4 PDF (Stickers Arranged)"
                          disabled={isPdfGenerating}
                        >
                          A4 PDF
                        </button>
                      </div>

                      <div className="absolute top-2 right-2 text-[8px] text-slate-300 font-bold font-mono">
                        4"x6"
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-100 flex justify-between items-center bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setShowPrintModal(false)}
                className="px-5 py-2.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold rounded-xl transition"
                disabled={isPdfGenerating}
              >
                Close Preview
              </button>

              <div className="flex gap-2 flex-wrap justify-end">
                {/* 1. Primary Download Option: A4 PDF Sheet (24 stickers per sheet) */}
                <button
                  onClick={() => downloadCombinedA4Pdf(printingItems)}
                  disabled={isPdfGenerating}
                  className="px-5 py-2.5 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-xs font-black rounded-xl shadow-md transition flex items-center gap-1.5"
                  title="Download all stickers arranged on A4 sheets (24 stickers per page)"
                >
                  {isPdfGenerating ? (
                    <Loader className="animate-spin" size={14} />
                  ) : (
                    <Download size={14} />
                  )}
                  Download A4 Sheet (24 per page)
                </button>

                {/* 2. Direct print option */}
                <button
                  onClick={() => window.print()}
                  disabled={isPdfGenerating}
                  className="px-5 py-2.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-xs font-black rounded-xl shadow-md transition flex items-center gap-1.5"
                >
                  <Printer size={14} /> Print Now
                </button>

                {/* 3. Secondary/Alternative format: Separate 4x6 pages */}
                <button
                  onClick={() => downloadCombinedPdf(printingItems)}
                  disabled={isPdfGenerating}
                  className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:bg-slate-50 disabled:text-slate-400 text-xs font-bold rounded-xl transition flex items-center gap-1.5"
                  title="Download as 4x6 single label cards (one sticker per page)"
                >
                  {isPdfGenerating ? (
                    <Loader className="animate-spin" size={14} />
                  ) : (
                    <Download size={14} />
                  )}
                  Download 4"x6" Cards (1 per page)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPrintModal && (
        <style dangerouslySetInnerHTML={{ __html: printLayout === 'A4' ? `
          @media print {
            body {
              background: white !important;
              color: black !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            /* Hide the main app content completely */
            #root {
              display: none !important;
            }
            /* Show print container */
            #barcode-print-container {
              display: block !important;
              width: 210mm !important;
              height: 297mm !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
            }
            .a4-print-page {
              width: 210mm !important;
              height: 297mm !important;
              page-break-after: always !important;
              page-break-inside: avoid !important;
              box-sizing: border-box !important;
              padding-left: 7.75mm !important;
              padding-right: 7.75mm !important;
              padding-top: 5.5mm !important;
              padding-bottom: 5.5mm !important;
              display: grid !important;
              grid-template-columns: repeat(3, 63.5mm) !important;
              grid-template-rows: repeat(8, 34.0mm) !important;
              column-gap: 2.0mm !important;
              row-gap: 2.0mm !important;
              background: white !important;
            }
            .a4-print-sticker {
              width: 63.5mm !important;
              height: 34.0mm !important;
              box-sizing: border-box !important;
              border: 0.1mm solid #cbd5e1 !important;
              padding: 1.5mm 2mm !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              align-items: center !important;
              text-align: center !important;
              background: white !important;
              overflow: hidden !important;
            }
            .a4-print-sticker-empty {
              width: 63.5mm !important;
              height: 34.0mm !important;
              box-sizing: border-box !important;
              border: 0.1mm dashed #e2e8f0 !important;
              visibility: hidden !important;
            }
            /* Hide backdrop/modals completely */
            .fixed, .backdrop-blur-sm, button {
              display: none !important;
            }
          }
        ` : `
          @media print {
            body {
              background: white !important;
              color: black !important;
            }
            /* Hide the main app content completely */
            #root {
              display: none !important;
            }
            /* Show print container */
            #barcode-print-container {
              display: block !important;
              width: 4in !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .sticker-page {
              width: 4in !important;
              height: 6in !important;
              page-break-after: always !important;
              page-break-inside: avoid !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: center !important;
              align-items: center !important;
              box-sizing: border-box !important;
              margin: 0 !important;
              padding: 0.5in 0.4in !important;
              background: white !important;
              border: none !important;
              gap: 0.25in !important;
            }
            /* Hide backdrop/modals completely */
            .fixed, .backdrop-blur-sm, button {
              display: none !important;
            }
          }
        ` }} />
      )}

      {showPrintModal && createPortal(
        <div id="barcode-print-container" className="hidden print:block bg-white">
          {printLayout === 'A4' ? (() => {
            const a4Pages: typeof printingItems[] = [];
            for (let i = 0; i < printingItems.length; i += 24) {
              a4Pages.push(printingItems.slice(i, i + 24));
            }
            return a4Pages.map((pageItems, pageIdx) => (
              <div key={pageIdx} className="a4-print-page">
                {Array.from({ length: 24 }).map((_, index) => {
                  const item = pageItems[index];
                  if (!item) {
                    return <div key={index} className="a4-print-sticker-empty" />;
                  }
                  return (
                    <div key={item.id} className="a4-print-sticker">
                      {/* Header */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.1' }}>
                        <span style={{ fontSize: '7.5pt', fontWeight: '900', color: '#334155', fontFamily: 'sans-serif' }}>Malayalee Samajam</span>
                        <span style={{ fontSize: '5.5pt', fontWeight: 'bold', color: '#64748b', fontFamily: 'sans-serif', letterSpacing: '0.5px', marginTop: '1px' }}>CAMP STAFF 2026</span>
                      </div>

                      {/* Barcode */}
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '11mm', width: '100%' }}>
                        {item.barcodeUrl ? (
                          <img src={item.barcodeUrl} alt={item.serialNo} style={{ height: '11mm', maxWidth: '100%', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ fontSize: '6pt', color: 'red' }}>Barcode Error</span>
                        )}
                      </div>

                      {/* Staff Name and Sub */}
                      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                        <h2 style={{ fontSize: '9.5pt', fontWeight: '900', color: '#0f172a', margin: '0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', fontFamily: 'sans-serif' }}>
                          {item.name.toUpperCase()}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                          <span style={{ fontSize: '6pt', fontWeight: '900', backgroundColor: '#f1f5f9', border: '0.1mm solid #cbd5e1', borderRadius: '1px', padding: '1px 3px', fontFamily: 'monospace' }}>
                            {item.role === 'faculty' ? 'FACULTY' : 'COOR'}
                          </span>
                          <span style={{ fontSize: '6pt', fontWeight: '900', color: '#0f172a', backgroundColor: '#f1f5f9', border: '0.1mm solid #cbd5e1', borderRadius: '1px', padding: '1px 3px', fontFamily: 'monospace' }}>
                            {item.serialNo}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })() : (
            printingItems.map((item) => (
              <div key={item.id} className="sticker-page">
                {/* Sticker Header */}
                <div className="text-center">
                  <h4 className="text-xs font-black uppercase tracking-widest text-slate-500">Malayalee Samajam</h4>
                  <p className="text-[10px] font-bold text-slate-400 tracking-wider mt-0.5">CAMP STAFF 2026</p>
                </div>

                {/* Barcode Image */}
                <div className="flex flex-col items-center justify-center w-full py-1">
                  {item.barcodeUrl ? (
                    <img src={item.barcodeUrl} alt={item.serialNo} className="w-[3.2in] h-auto object-contain" />
                  ) : (
                    <div className="text-xs text-red-500 font-mono">Barcode Error</div>
                  )}
                </div>

                {/* Staff Name & ID Info */}
                <div className="text-center w-full">
                  <h2 className="text-2xl font-black tracking-tight text-slate-900 uppercase break-words leading-tight" style={{ fontSize: '26px' }}>
                    {item.name}
                  </h2>
                  <div className="mt-1 flex items-center justify-center gap-2">
                    <span className="px-2 py-0.5 bg-slate-100 border border-slate-300 text-[11px] font-black uppercase tracking-wider rounded font-mono">
                      {item.role === 'faculty' ? 'FACULTY' : 'COORDINATOR'}
                    </span>
                    <span className="text-[11px] font-black tracking-widest font-mono text-slate-700 bg-slate-100 border border-slate-300 px-2 py-0.5 rounded">
                      {item.serialNo}
                    </span>
                  </div>
                </div>

                {/* Small footer to look authentic */}
                <div className="text-center text-[8px] font-bold text-slate-400 tracking-widest uppercase mt-4">
                  MALAYALEE SAMAJAM CAMP MANAGEMENT SYSTEM
                </div>
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default StaffManager;
